import { useMemo, useRef } from 'react'
import { matchPath } from 'react-router'
import { UnfurledLinkAttachment } from '@towns-protocol/sdk'
import { UnfurledLink, useUnfurlContent } from 'api/lib/unfurl'
import { isEqual, uniq, uniqBy } from 'lodash-es'
import { PATHS } from 'routes'
import { notUndefined } from 'ui/utils/utils'
import { isTownsAppUrlString } from 'utils/isTownsAppUrl'

export type EmbeddedMessageLink = {
    type: 'message'
    id: string
    pathname: string
    spaceId: string
    channelId: string
    messageId: string
    url: string
}

export type LoadingUnfurledLinkAttachment = UnfurledLinkAttachment & { isLoading: true }

export const useExtractInternalLinks = (text: string): EmbeddedMessageLink[] | undefined => {
    const links = getTownsLinks(text)
    const linksRef = useRef(links)
    return useMemo(() => {
        if (!links) {
            return undefined
        }
        const cleanLinks = uniqBy(links, 'id')
        return isEqual(cleanLinks, linksRef.current)
            ? linksRef.current
            : (linksRef.current = cleanLinks)
    }, [links])
}

export const useExtractExternalLinks = (
    text: string,
): {
    attachments: (UnfurledLinkAttachment | LoadingUnfurledLinkAttachment)[] | undefined
    isLoading: boolean
} => {
    const links = getExternalLinks(text)

    const linksRef = useRef(links?.map((l) => l.href))
    const cleanLinks = useMemo(() => {
        if (!links) {
            return undefined
        }
        const hrefLinks = links.map((l) => l.href)

        const cleanLinks = uniq(hrefLinks)
        if (!isEqual(cleanLinks, linksRef.current)) {
            linksRef.current = cleanLinks
        }
        return linksRef.current
    }, [links])

    // async fetch unfurled content
    const {
        data: unfurledLinksQuery,
        isError,
        isLoading,
    } = useUnfurlContent({
        urlsArray: Array.from(new Set(cleanLinks || [])),
        enabled: !!cleanLinks && cleanLinks.length > 0,
    })

    const unfurledLinks = useMemo(() => {
        if (!unfurledLinksQuery || !Array.isArray(unfurledLinksQuery) || isError) {
            return cleanLinks?.map((link) => {
                return {
                    type: 'unfurled_link',
                    isLoading: true,
                    url: link,
                    id: link,
                } as LoadingUnfurledLinkAttachment
            })
        } else {
            const attachments = unfurledLinksQuery.map((value) => {
                const unfurledValue = value as UnfurledLink
                const isMiniapp = unfurledValue.type === 'miniapp'

                // Create base attachment
                const baseAttachment: UnfurledLinkAttachment = {
                    type: 'unfurled_link',
                    url: value.url,
                    title: value.title ?? '',
                    description: value.description ?? '',
                    image: value.image,
                    id: value.url,
                }

                // Preserve additional unfurl data (like miniapp info) as extra properties
                // Keep the base attachment type and serialize the info as JSON string for SDK compatibility
                if (isMiniapp && 'info' in unfurledValue) {
                    const attachmentWithInfo = {
                        ...baseAttachment,
                        info: JSON.stringify(unfurledValue.info),
                    }
                    return attachmentWithInfo
                }

                return baseAttachment
            })
            return attachments
        }
    }, [unfurledLinksQuery, isError, cleanLinks])

    return { attachments: unfurledLinks, isLoading }
}

const matchTextLinks =
    /\[[^\]]+\]\((?<mdLink>https:\/\/[^\s)]+)\)|(?<simpleLink>https:\/\/[^\s)]+)/g

function getTownsLinks(text: string) {
    return getExternalLinks(text)
        ?.filter((u) => isTownsAppUrlString(u.href))
        .map(parseTownLink)
        .filter(notUndefined)
}

export function getExternalLinks(text: string) {
    const urls = Array.from(text.matchAll(matchTextLinks))
        .map((u) => {
            if (u) {
                try {
                    const link = u.groups?.mdLink || u.groups?.simpleLink
                    if (link) {
                        const url = new URL(link)
                        return url
                    }
                } catch (e) {
                    // ignore, trivial error
                }
            }
        })
        .filter(notUndefined)
        .filter((url) => !url.href.includes('tel.meet/'))

    if (urls.length === 0) {
        return undefined
    }

    return urls
}

const parseTownLink = (url: URL) => {
    let messageId = url.hash.replace('#', '')
    messageId = messageId.match(/[a-f0-9]{64}/i) ? messageId : ''

    const matchChannel = matchPath(
        `/${PATHS.SPACES}/:spaceId/${PATHS.CHANNELS}/:channelId`,
        url.pathname,
    )

    const matchDM = matchPath(`/messages/:channelId`, url.pathname)

    const matchReplies = matchPath(
        `/${PATHS.SPACES}/:spaceId/${PATHS.CHANNELS}/:channelId/${PATHS.REPLIES}/:messageId`,
        url.pathname,
    )

    if (matchDM) {
        const matchParams = {
            ...matchDM.params,
            messageId,
            isReply: !!matchReplies,
        }

        return matchParams.channelId && matchParams.messageId
            ? {
                  type: 'message' as const,
                  id: `message-${matchParams.messageId}`,
                  pathname: url.pathname,
                  spaceId: '',
                  channelId: matchParams.channelId,
                  messageId: matchParams.messageId,
                  url: url.href,
              }
            : undefined
    }

    const match = matchChannel || matchReplies

    if (match) {
        const matchParams = {
            ...match.params,
            messageId,
            isReply: !!matchReplies,
        }

        return matchParams.spaceId && matchParams.channelId && matchParams.messageId
            ? {
                  type: 'message' as const,
                  id: `message-${matchParams.messageId}`,
                  pathname: url.pathname,
                  spaceId: matchParams.spaceId,
                  channelId: matchParams.channelId,
                  messageId: matchParams.messageId,
                  url: url.href,
              }
            : undefined
    }
}
